import * as React from 'react';
import styled from 'styled-components';
import { BuildCourseContext } from 'contextProviders/BuildCourseContext';
import { BodyRegular, FlexColMd, FlexEndMd } from 'components/Elements';
import Table from 'components/table/Table';
import strings from '../../../strings/strings.json';
import { ErrorNotifierSlim } from 'components/notifiers/ErrorNotifierSlim';
import {
    addNewActivities,
    addNewModules,
    deleteModuleActivities,
    deleteModules,
    editActivities,
    editModuleTitleAndDescription,
    learningObjectives,
    ReviewItem,
    titleAndDescription,
} from './ReviewChangesFunctions';
import Button from 'components/buttons/Button';

type ReviewChangesModalProps = {
    saving: boolean;
    editLiveCourseError: boolean;
    onConfirm: () => void;
    closeModal: () => void;
};

export default function ReviewChanges({
    saving,
    editLiveCourseError,
    onConfirm,
    closeModal,
}: ReviewChangesModalProps) {
    const origin = React.useContext(BuildCourseContext).snapshot!;
    const edit = React.useContext(BuildCourseContext).course!;

    let reviewItems: ReviewItem[] = [];

    titleAndDescription(reviewItems, origin, edit);
    learningObjectives(reviewItems, origin, edit);

    //DELETIONS
    deleteModules(reviewItems, origin, edit);
    deleteModuleActivities(reviewItems, origin, edit);

    //EDITS
    editModuleTitleAndDescription(reviewItems, origin, edit);
    editActivities(reviewItems, origin, edit);

    //ADDITIONS
    addNewModules(reviewItems, edit);
    addNewActivities(reviewItems, edit);

    //Place modules in correct order.
    reviewItems = reviewItems.sort((a, b) => {
        if (a.label.startsWith('Module') && b.label.startsWith('Module')) {
            const orderOfA = parseInt(a.label.replace(/\D+/g, ''), 10);
            const orderOfB = parseInt(b.label.replace(/\D+/g, ''), 10);
            return orderOfA - orderOfB;
        }
        return 0;
    });

    return (
        <>
            <Table
                headers={[
                    { name: strings.reviewChangesModal.tableHeaders.field, rowPercentage: 20 },
                    { name: strings.reviewChangesModal.tableHeaders.before, rowPercentage: 40 },
                    { name: strings.reviewChangesModal.tableHeaders.after, rowPercentage: 40 },
                ]}
                rows={reviewItems.map((d) => ({
                    id: d.label,
                    cells: [
                        {
                            name: strings.reviewChangesModal.tableHeaders.field,
                            value: <BodyRegularTopAligned $bold>{d.label}</BodyRegularTopAligned>,
                        },
                        {
                            name: strings.reviewChangesModal.tableHeaders.before,
                            value: <BodyRegularTopAligned>{d.before}</BodyRegularTopAligned>,
                        },
                        {
                            name: strings.reviewChangesModal.tableHeaders.after,
                            value: <BodyRegularTopAligned>{d.after}</BodyRegularTopAligned>,
                        },
                    ],
                }))}
            />
            <ErrorContainer>
                {editLiveCourseError && <ErrorNotifierSlim description={strings.reviewChangesModal.errorMessage} />}
                <ButtonsRow>
                    <Button
                        title={'Back to editor'}
                        aria={strings.reviewChangesModal.cancelButton.aria}
                        onClick={closeModal}
                    />
                    <Button
                        title={strings.reviewChangesModal.confirmButton.title}
                        aria={strings.reviewChangesModal.confirmButton.aria}
                        colour='PRIMARY'
                        onClick={onConfirm}
                        disabled={saving}
                    />
                </ButtonsRow>
            </ErrorContainer>
        </>
    );
}

const ButtonsRow = styled(FlexEndMd)`
    align-items: center;
`;

const ErrorContainer = styled(FlexColMd)`
    align-items: end;
`;

const BodyRegularTopAligned = styled(BodyRegular)`
    align-self: start;
`;
